import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CornerDownLeft, Loader2, Check } from "lucide-react";
import { getUtm } from "@/lib/utm";
import { trackLead } from "@/lib/consent";
import { Confirmation } from "@/components/form/Confirmation";
import { FullName } from "@/components/form/FullName";
import { API } from "@/lib/api";
import { chatAttribution } from "@/lib/chat";

export const SETTORI = [
  "Industria e produzione", "Artigianato ed edilizia", "Commercio e retail", "Logistica e trasporti",
  "Ristorazione e hospitality", "Estetica e benessere", "Servizi alle imprese/B2B", "Sanità e studi professionali", "Altro",
];
const TEMPI = ["Subito", "Entro 1 mese", "Entro 3 mesi", "Sto valutando"];
const PHONE_RE = /^(\+39|0039)?((3\d{8,9})|(0\d{5,10}))$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const STEPS = [
  { key: "nome", label: "Come ti chiami?", type: "fullname", req: true },
  { key: "azienda", label: "Azienda", type: "text", ac: "organization", ph: "Nome dell'azienda", req: true },
  { key: "telefono", label: "Telefono", type: "tel", ac: "tel", ph: "333 123 4567", req: true },
  { key: "email", label: "Email", type: "email", ac: "email", ph: "nome@azienda.it", req: true },
  { key: "settore", label: "Settore", type: "choice", options: SETTORI, req: true },
  { key: "zona", label: "Sede (città/provincia)", type: "text", ac: "address-level2", ph: "Es. Perugia (PG)", req: true },
  { key: "figura", label: "Che figura cerchi?", type: "textarea", ph: "Es. 2 magazzinieri con patentino muletto", req: true },
  { key: "tempistica", label: "Quando ti serve?", type: "pills", options: TEMPI },
  { key: "note", label: "Note", type: "textarea", ph: "Facoltativo: orari, contratto, qualsiasi dettaglio utile" },
  { key: "privacy", label: "Ultimo passo", type: "privacy", req: true },
];

const validate = (key, v) => {
  const s = typeof v === "string" ? v.trim() : v;
  switch (key) {
    case "nome": return s.length < 2 ? "Scrivi il tuo nome." : "";
    case "cognome": return s.length < 2 ? "Scrivi il tuo cognome." : "";
    case "azienda": return !s ? "Scrivi il nome della tua azienda." : "";
    case "telefono": return !PHONE_RE.test(s.replace(/[\s\-./()]/g, "")) ? "Inserisci un numero italiano valido, es. 333 123 4567." : "";
    case "email": return !EMAIL_RE.test(s) ? "Inserisci un'email valida, es. nome@azienda.it." : "";
    case "settore": return !s ? "Scegli un settore." : "";
    case "zona": return s.length < 2 ? "Scrivi città o provincia." : "";
    case "figura": return s.length < 3 ? "Descrivi in breve la figura che cerchi." : "";
    case "privacy": return !v ? "Per inviare la richiesta devi accettare l'informativa privacy." : "";
    default: return "";
  }
};

const EMPTY = { nome: "", cognome: "", azienda: "", telefono: "", email: "", settore: "", zona: "", figura: "", tempistica: "", note: "", privacy: false };

const Choice = ({ step, value, onPick }) => (
  <div role="radiogroup" aria-label={step.label} className={step.type === "pills" ? "flex flex-wrap gap-2.5" : "grid gap-2 sm:grid-cols-2"}>
    {step.options.map((o, i) => {
      const on = value === o;
      return (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={on}
          data-option
          onClick={() => onPick(o)}
          data-testid={`lead-${step.key}-option-${i}`}
          className={`group flex items-center gap-3 border text-left text-[15px] transition-[background-color,border-color,color,transform] duration-200 active:scale-[.98] ${
            step.type === "pills" ? "rounded-full px-5 py-3" : "rounded-xl px-4 py-3"
          } ${on ? "border-[#0F6E56] bg-[#0F6E56] text-white" : "border-[#E7E5DF] bg-white hover:border-[#0F6E56] hover:bg-[#BCE3D6]/30"}`}
        >
          {step.type === "choice" && (
            <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border font-mono-data text-[11px] ${on ? "border-white/40 text-white" : "border-[#E7E5DF] text-[#6B6961]"}`}>
              {String.fromCharCode(65 + i)}
            </span>
          )}
          {o}
          {on && <Check size={15} className="ml-auto" />}
        </button>
      );
    })}
  </div>
);

const Field = ({ step, value, set, error, inputRef, autoFocusAttr }) => {
  const common = {
    ref: inputRef, id: `lead-${step.key}`, value, onChange: (e) => set(e.target.value), placeholder: step.ph,
    "aria-invalid": !!error, "aria-describedby": error ? `lead-${step.key}-err` : undefined, "data-testid": `lead-input-${step.key}`,
    className: "w-full border-0 border-b-2 border-[#E7E5DF] bg-transparent px-0 pb-3 pt-1 font-display text-2xl text-[#2C2C2A] outline-none transition-colors placeholder:text-[#8A887F]/70 focus:border-[#0F6E56] focus-visible:outline-none sm:text-[28px]",
    ...(autoFocusAttr ? { "data-lead-autofocus": true } : {}),
  };
  if (step.type === "textarea") return <textarea rows={2} {...common} className={`${common.className} resize-none`} />;
  return <input type={step.type} autoComplete={step.ac} {...common} />;
};

export const LeadForm = ({ fonte = "sito_web", pagina }) => {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [error, setError] = useState("");
  const [errField, setErrField] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);
  const interacted = useRef(false);
  const step = STEPS[i];

  useEffect(() => {
    if (!interacted.current) return;
    const el = inputRef.current || document.querySelector(`[data-step-panel] button, [data-step-panel] input`);
    if (el) el.focus({ preventScroll: true });
  }, [i]);

  const set = (v) => { setData((d) => ({ ...d, [step.key]: v })); if (error) setError(""); };

  const setName = (k, v) => { setData((d) => ({ ...d, [k]: v })); if (errField === k) { setError(""); setErrField(null); } };

  const nextName = () => {
    for (const k of ["nome", "cognome"]) {
      const err = validate(k, data[k]);
      if (err) {
        if (k === "cognome" && document.activeElement && document.activeElement.id === "lead-nome" && !data.cognome.trim()) {
          document.getElementById("lead-cognome").focus();
          return false;
        }
        setError(err); setErrField(k);
        document.getElementById(`lead-${k}`).focus();
        return false;
      }
    }
    setError(""); setErrField(null);
    return true;
  };

  const next = (override) => {
    interacted.current = true;
    if (step.type === "fullname") {
      if (!nextName()) return;
      setDir(1); setI(i + 1); return;
    }
    const v = override !== undefined ? override : data[step.key];
    const err = validate(step.key, v);
    if (err) { setError(err); return; }
    setError("");
    if (i === STEPS.length - 1) { submit(); return; }
    setDir(1);
    setI(i + 1);
  };
  const back = () => { interacted.current = true; setError(""); setErrField(null); setDir(-1); setI(Math.max(0, i - 1)); };

  const pick = (o) => {
    setData((d) => ({ ...d, [step.key]: o }));
    setError("");
    setTimeout(() => next(o), 260);
  };

  const submit = async () => {
    setSending(true);
    try {
      const body = { ...data, nome: data.nome.trim(), cognome: data.cognome.trim(), telefono: data.telefono.replace(/[\s\-./()]/g, ""), fonte, pagina: pagina || window.location.href, ...getUtm(), data_invio: new Date().toISOString(), ...(chatAttribution() || {}) };
      const res = await fetch(`${API}/leads`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg = Array.isArray(j.detail) ? j.detail.map((d) => (d.msg || "").replace("Value error, ", "")).join(" ") : j.detail;
        throw new Error(msg || "Qualcosa non ha funzionato. Riprova tra poco.");
      }
      trackLead();
      setDone(true);
    } catch (e) {
      setError(e.message || "Qualcosa non ha funzionato. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey || e.target.tagName === "BUTTON" || e.target.tagName === "A") return;
    if (e.target.type === "checkbox") return;
    e.preventDefault();
    next();
  };

  const onChoiceKey = (e) => {
    if (step.type !== "choice" || e.target.tagName === "INPUT") return;
    const idx = e.key.toUpperCase().charCodeAt(0) - 65;
    if (e.key.length === 1 && idx >= 0 && idx < step.options.length) pick(step.options[idx]);
  };

  if (done) return <Confirmation name={data.nome.trim()} onReset={() => { setData(EMPTY); setI(0); setDone(false); interacted.current = false; }} />;

  const progress = ((i + 1) / STEPS.length) * 100;
  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); next(); }} onKeyDown={(e) => { onKeyDown(e); onChoiceKey(e); }} className="flex min-h-[460px] flex-col" data-testid="lead-form" aria-label="Richiedi una figura">
      <div className="flex items-center justify-between font-mono-data text-[12px] uppercase tracking-[0.14em] text-[#6B6961]">
        <span data-testid="lead-step-indicator">{String(i + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}</span>
        <span>{step.req ? "Obbligatorio" : "Facoltativo"}</span>
      </div>
      <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-[#E7E5DF]" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} aria-label="Avanzamento">
        <div className="h-full rounded-full bg-[#0F6E56] transition-[width] duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="lead-progress-bar" />
      </div>

      <div key={i} data-step-panel className="mt-10 flex-1" style={{ animation: `${dir > 0 ? "rl-step-in" : "rl-step-back"} .5s cubic-bezier(.16,1,.3,1) both` }}>
        {step.type === "fullname" ? (
          <p className="block font-display text-[26px] leading-tight tracking-[-0.02em] sm:text-[32px]" data-testid="lead-step-label">{step.label}</p>
        ) : (
          <label htmlFor={`lead-${step.key}`} className="block font-display text-[26px] leading-tight tracking-[-0.02em] sm:text-[32px]" data-testid="lead-step-label">
            {step.label}{step.req && step.type !== "privacy" && <span className="text-[#0F6E56]">*</span>}
          </label>
        )}
        <div className="mt-7">
          {(step.type === "text" || step.type === "tel" || step.type === "email" || step.type === "textarea") && (
            <Field step={step} value={data[step.key]} set={set} error={error} inputRef={inputRef} autoFocusAttr={i === 0} />
          )}
          {step.type === "fullname" && <FullName data={data} onChange={setName} errField={errField} inputRef={inputRef} autoFocusAttr={i === 0} />}
          {(step.type === "choice" || step.type === "pills") && <Choice step={step} value={data[step.key]} onPick={pick} />}
          {step.type === "privacy" && (
            <label className="flex cursor-pointer items-start gap-4 rounded-2xl border border-[#E7E5DF] bg-[#F7F6F2] p-5" data-testid="lead-privacy-label">
              <input
                ref={inputRef}
                id="lead-privacy"
                type="checkbox"
                checked={data.privacy}
                onChange={(e) => set(e.target.checked)}
                aria-invalid={!!error}
                aria-describedby={error ? "lead-privacy-err" : undefined}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#0F6E56]"
                data-testid="lead-input-privacy"
              />
              <span className="text-[15px] leading-relaxed">
                Ho letto l'<Link to="/privacy" className="font-medium text-[#0F6E56] underline underline-offset-4" data-testid="lead-privacy-link">informativa privacy</Link> e acconsento al trattamento dei miei dati per essere ricontattato.*
              </span>
            </label>
          )}
          {error && (
            <p id={`lead-${step.key}-err`} role="alert" className="mt-4 text-[14px] font-medium text-[#B42318]" data-testid="lead-error">
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between gap-3">
        <button type="button" onClick={back} disabled={i === 0} className="inline-flex h-12 items-center gap-2 rounded-full px-4 text-[14px] font-medium text-[#2C2C2A] transition-colors hover:bg-[#F7F6F2] disabled:pointer-events-none disabled:opacity-30" data-testid="lead-back-button">
          <ArrowLeft size={16} /> Indietro
        </button>
        <div className="flex items-center gap-4">
          <span className="hidden items-center gap-1.5 font-mono-data text-[11px] uppercase tracking-[0.12em] text-[#6B6961] md:inline-flex">
            Premi Invio <CornerDownLeft size={12} />
          </span>
          {step.type === "privacy" ? (
            <button type="submit" disabled={sending} className="rl-btn rl-btn-amber" data-testid="lead-submit-button">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Invio…</> : <>Invia la richiesta <ArrowRight size={16} className="rl-arrow" /></>}
            </button>
          ) : (
            <button type="submit" className="rl-btn bg-[#2C2C2A] text-white hover:bg-[#0F6E56]" data-testid="lead-next-button">
              {step.req || data[step.key] ? "Avanti" : "Salta"} <ArrowRight size={16} className="rl-arrow" />
            </button>
          )}
        </div>
      </div>
    </form>
  );
};
